import { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { fs } from "@/lib/firebase";
import type { Product, Rubric } from "@/lib/types";
import type { ParsedQr } from "@/lib/qrParse";

export interface RubricCatalog {
  byId: Map<string, Rubric>;
  loaded: boolean;
}

/**
 * Toutes les rubriques du restaurant, chargées une fois et tenues à jour —
 * comme `this.options` sur la tablette (Restaurant.js:812, 827-863).
 *
 * Remplace le chargement à chaque ouverture du modal, qui laissait le produit
 * partir sans ses options obligatoires quand la requête échouait en silence,
 * et permet à l'ajout rapide de savoir si un choix est obligatoire.
 */
export const useRubricCatalog = (parsed: ParsedQr | null, takeawayMenu: boolean): RubricCatalog => {
  const [byId, setById] = useState<Map<string, Rubric>>(new Map());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!parsed) return;
    setLoaded(false);
    const colName = takeawayMenu ? "RubricTakeaway" : "Rubric";
    const ref = collection(fs, `Restaurant/${parsed.restoId}/${colName}`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const m = new Map<string, Rubric>();
        snap.forEach((d) => m.set(d.id, { id: d.id, ...(d.data() as Omit<Rubric, "id">) }));
        setById(m);
        setLoaded(true);
      },
      (err) => {
        console.error("[useRubricCatalog] snapshot failed", err);
        setLoaded(true);
      },
    );
    return unsub;
  }, [parsed?.restoId, takeawayMenu]);

  return { byId, loaded };
};

/**
 * Rubriques d'un produit, dans l'ordre de `product.rubrics` (ProductDetail.js:130-143) ;
 * les ids priment sur les rubriques embarquées `product.rubric` (ancien schéma).
 */
export const resolveProductRubrics = (product: Product | null, catalog: RubricCatalog): Rubric[] => {
  if (!product) return [];
  const ids = product.rubrics ?? [];
  if (ids.length) {
    const out: Rubric[] = [];
    for (const id of ids) {
      const r = catalog.byId.get(id);
      if (r) out.push(r);
    }
    return out;
  }
  if (Array.isArray(product.rubric) && product.rubric.length) {
    return product.rubric.map((r, i) => ({ ...r, id: r.id ?? `embedded-${i}` }));
  }
  return [];
};

/** Un choix obligatoire existe-t-il ? (tablette handleQuickAdd, Restaurant.js:1057-1066) */
export const hasRequiredChoice = (product: Product, catalog: RubricCatalog): boolean =>
  resolveProductRubrics(product, catalog).some((r) => r.necessary === true);
